import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getLeague } from '../services/leagueService';
import { updateUserProfile } from '../services/userService';
import { cancelAccountDeletion } from '../services/accountService';
import GroupStageTab from '../components/GroupStageTab';
import LeaderboardTab from '../components/LeaderboardTab';
import MyPicksTab from '../components/MyPicksTab';
import BracketTab from '../components/BracketTab';
import './LeaguePage.css';

const TABS = [
  { key: 'group',      label: 'Group Stage' },
  { key: 'bracket',   label: 'Bracket' },
  { key: 'leaderboard', label: 'Leaderboard' },
  { key: 'picks',     label: 'My Picks' },
];

export default function LeaguePage() {
  const { leagueId } = useParams();
  const { currentUser, userProfile } = useAuth();
  const isDark = !!userProfile?.darkMode;

  async function toggleDarkMode() {
    if (!currentUser) return;
    try {
      await updateUserProfile(currentUser.uid, { darkMode: !isDark });
    } catch (_) {
      // non-critical — ignore silently
    }
  }
  const navigate = useNavigate();

  const [league, setLeague] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activeTab, setActiveTab] = useState('group');
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState(false);

  useEffect(() => {
    if (!leagueId) return;
    getLeague(leagueId)
      .then((data) => {
        if (!data) setNotFound(true);
        else setLeague(data);
      })
      .catch(() => setNotFound(true));
  }, [leagueId]);

  if (notFound) {
    return (
      <div className="league-page">
        <div className="league-not-found">League not found.</div>
      </div>
    );
  }

  if (!league) {
    return (
      <div className="league-page">
        <div className="league-loading">Loading…</div>
      </div>
    );
  }

  const isCommissioner = currentUser?.uid === league.commissionerId;
  const isLocked = league.settingsLocked;

  async function handleCancelDeletion() {
    setCancelLoading(true);
    setCancelError(false);
    try {
      await cancelAccountDeletion(league.commissionerId);
      setLeague((prev) => ({ ...prev, pendingDeletion: false, deletionExecuteAt: null }));
    } catch (_) {
      setCancelError(true);
    } finally {
      setCancelLoading(false);
    }
  }

  return (
    <div className="league-page">
      {/* ── Pending deletion banner ── */}
      {league.pendingDeletion && isCommissioner && (
        <LeagueDeletionBanner
          executionAt={league.deletionExecuteAt}
          isCommissioner={isCommissioner}
          onCancel={handleCancelDeletion}
          cancelLoading={cancelLoading}
          cancelError={cancelError}
        />
      )}

      {/* ── Header ── */}
      <header className="league-header">
        <span className="league-header-name">{league.name}</span>

        {/* Commissioner Manage button — visible pre-lock only */}
        {isCommissioner && !isLocked && (
          <button
            className="league-header-btn league-header-btn--manage"
            onClick={() => navigate(`/league/${leagueId}/manage`)}
          >
            Manage
          </button>
        )}

        <button
          className="league-header-btn"
          onClick={toggleDarkMode}
          title="Toggle dark mode"
          aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {isDark
            ? <svg viewBox="0 0 16 16" width="15" height="15" fill="currentColor" aria-hidden="true"><path d="M8 1a1 1 0 0 1 1 1v1a1 1 0 0 1-2 0V2a1 1 0 0 1 1-1zm0 10a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm6-3a1 1 0 0 1-1 1h-1a1 1 0 0 1 0-2h1a1 1 0 0 1 1 1zM3 8a1 1 0 0 1-1 1H1a1 1 0 0 1 0-2h1a1 1 0 0 1 1 1zm9.19-4.78a1 1 0 0 1 0 1.42l-.71.7a1 1 0 1 1-1.41-1.41l.7-.71a1 1 0 0 1 1.42 0zM5.63 11.8a1 1 0 0 1 0 1.41l-.7.71a1 1 0 1 1-1.42-1.42l.71-.7a1 1 0 0 1 1.41 0zm6.29 1.41a1 1 0 0 1-1.41 0l-.71-.7a1 1 0 0 1 1.41-1.42l.71.71a1 1 0 0 1 0 1.41zM5.63 4.22a1 1 0 0 1-1.41 0l-.71-.7A1 1 0 0 1 4.92 2.1l.71.71a1 1 0 0 1 0 1.41z"/></svg>
            : <svg viewBox="0 0 16 16" width="15" height="15" fill="currentColor" aria-hidden="true"><path d="M6 .278a.77.77 0 0 1 .08.858 7.208 7.208 0 0 0-.878 3.46c0 4.021 3.278 7.277 7.318 7.277q.792-.001 1.533-.16a.787.787 0 0 1 .81.316.733.733 0 0 1-.031.893A8.349 8.349 0 0 1 8.344 16C3.734 16 0 12.286 0 7.71 0 4.266 2.114 1.312 5.124.06A.752.752 0 0 1 6 .278z"/></svg>
          }
        </button>
        <button
          className="league-header-btn"
          onClick={() => navigate('/account')}
          title="My account"
          aria-label="My account"
        >
          <svg viewBox="0 0 16 16" width="15" height="15" fill="currentColor" aria-hidden="true">
            <circle cx="8" cy="5.5" r="2.8"/>
            <path d="M2 14.5c0-3.3 2.7-6 6-6s6 2.7 6 6H2z"/>
          </svg>
        </button>
        <button
          className="league-header-btn"
          onClick={() => navigate('/leagues', { state: { showSelector: true } })}
          title="Switch league"
          aria-label="Switch league"
        >
          <svg viewBox="0 0 16 16" width="15" height="15" fill="currentColor" aria-hidden="true">
            <path d="M8 1.5 1.5 7H3v7h4v-3.5h2V14h4V7h1.5L8 1.5z"/>
          </svg>
        </button>
      </header>

      {/* ── Tab bar ── */}
      <nav className="league-tabs" role="tablist">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            role="tab"
            aria-selected={activeTab === tab.key}
            className={`league-tab${activeTab === tab.key ? ' active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {/* ── Tab content ── */}
      <main className="league-content" role="tabpanel">
        <TabContent tab={activeTab} leagueId={leagueId} uid={currentUser?.uid} />
      </main>
    </div>
  );
}

function TabContent({ tab, leagueId, uid }) {
  if (tab === 'group')       return <GroupStageTab leagueId={leagueId} uid={uid} />;
  if (tab === 'leaderboard') return <LeaderboardTab leagueId={leagueId} uid={uid} />;
  if (tab === 'picks')       return <MyPicksTab leagueId={leagueId} uid={uid} />;
  if (tab === 'bracket')     return <BracketTab leagueId={leagueId} uid={uid} />;
  return null;
}

function LeagueDeletionBanner({ executionAt, isCommissioner, onCancel, cancelLoading, cancelError }) {
  const dateStr = executionAt
    ? new Date(executionAt.seconds ? executionAt.seconds * 1000 : executionAt).toLocaleString('en-US', {
        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
      })
    : '';

  return (
    <div className="league-deletion-banner">
      <div className="league-deletion-banner-text">
        <strong>This league is scheduled for deletion</strong>
        {dateStr && ` on ${dateStr}`}.
        {' '}The commissioner has requested account deletion. All picks and members will lose access.
      </div>
      {isCommissioner && (
        <>
          {cancelError && (
            <div className="league-deletion-cancel-error">Cancellation failed — please try again.</div>
          )}
          <button
            className="league-deletion-cancel-btn"
            onClick={onCancel}
            disabled={cancelLoading}
          >
            {cancelLoading ? 'Cancelling…' : 'Cancel Deletion'}
          </button>
        </>
      )}
    </div>
  );
}
